
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToast } from '../../components/common/ToastContext';
import { Turn, Inspection, InspectionSchedule, TurnTask } from '@repo/shared-types';
import { turnTaskSchema } from '@repo/shared-types/maintenance/schemas';
import { turnSchema } from '@repo/shared-types/maintenance/operationSchemas';
import { inspectionSchema } from '@repo/shared-types/maintenance/operationSchemas';
import { validateEntity, validateEntityArray } from '@repo/shared-utils';
import { logger } from '@repo/shared-utils';

export const useTurns = () => {
  return useQuery({
    queryKey: ['maintenance', 'turns'],
    queryFn: async () => {
      const data = await api.maintenance.turns.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(turnSchema, data);
      if (!validation.valid) {
        logger.error('Invalid turns query response:', validation.errors);
        throw new Error('Invalid turns data received from API');
      }
      return validation.data! as Turn[];
    },
  });
};

export const useCreateTurn = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (turn: Turn) => api.maintenance.turns.create(turn),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(turnSchema, data);
      if (!validation.valid) {
        logger.error('Invalid turn response from API:', validation.errors);
        showToast('Turn created but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
      showToast('Turn created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create turn', 'error');
    }
  });
};

export const useUpdateTurn = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Turn> }) => api.maintenance.turns.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(turnSchema, data);
      if (!validation.valid) {
        logger.error('Invalid turn response from API:', validation.errors);
        showToast('Turn updated but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
      showToast('Turn status updated', 'success');
    }
  });
};

export const useInspections = () => {
  return useQuery({
    queryKey: ['maintenance', 'inspections'],
    queryFn: async () => {
      const data = await api.maintenance.inspections.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(inspectionSchema, data);
      if (!validation.valid) {
        logger.error('Invalid inspections query response:', validation.errors);
        throw new Error('Invalid inspections data received from API');
      }
      return validation.data! as Inspection[];
    },
  });
};

export const useCreateInspection = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (data: Omit<Inspection, 'id'>) => api.maintenance.inspections.create(data),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(inspectionSchema, data);
      if (!validation.valid) {
        logger.error('Invalid inspection response from API:', validation.errors);
        showToast('Inspection scheduled but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections'] });
      showToast('Inspection scheduled successfully', 'success');
    }
  });
};

export const useUpdateInspection = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Inspection> }) => api.maintenance.inspections.update(id, updates),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(inspectionSchema, data);
      if (!validation.valid) {
        logger.error('Invalid inspection response from API:', validation.errors);
        showToast('Inspection updated but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections'] });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections'] });
      showToast('Inspection updated', 'success');
    }
  });
};

export const useDeleteInspection = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.maintenance.inspections.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections'] });
      showToast('Inspection deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete inspection', 'error');
    }
  });
};

// TURN TASKS
export const useTurnTasks = (turnId: string | undefined) => {
  return useQuery({
    queryKey: ['maintenance', 'turns', turnId, 'tasks'],
    queryFn: async () => {
      const data = await api.maintenance.turns.tasks.list(turnId!);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(turnTaskSchema, data);
      if (!validation.valid) {
        logger.error('Invalid turn tasks query response:', validation.errors);
        throw new Error('Invalid turn tasks data received from API');
      }
      return validation.data! as TurnTask[];
    },
    enabled: !!turnId,
  });
};

export const useCreateTurnTask = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ turnId, taskData }: { turnId: string; taskData: { title: string; description?: string; assignedVendorId?: string; dueDate?: string } }) =>
      api.maintenance.turns.tasks.create(turnId, { ...taskData, turnId, status: 'Not Started' }),
    onSuccess: (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(turnTaskSchema, data);
      if (!validation.valid) {
        logger.error('Invalid turn task response from API:', validation.errors);
        showToast('Task created but received invalid data from server', 'error');
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
        return;
      }
      const task: TurnTask = validation.data!;
      if (task.turnId) {
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns', task.turnId, 'tasks'] });
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
      }
      showToast('Task created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create task', 'error');
    }
  });
};

export const useUpdateTurnTask = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<{ title: string; description?: string; status: 'Not Started' | 'In Progress' | 'Completed'; assignedVendorId?: string; dueDate?: string }> }) =>
      api.maintenance.turns.tasks.update(id, updates),
    onSuccess: (data) => {
      // Validate API response with Zod schema
      const validation = validateEntity(turnTaskSchema, data);
      if (!validation.valid) {
        logger.error('Invalid turn task response from API:', validation.errors);
        showToast('Task updated but received invalid data from server', 'error');
        // Still invalidate queries even if validation fails
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
        return;
      }

      // Get turnId from validated task
      const task: TurnTask = validation.data!;
      if (task.turnId) {
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns', task.turnId, 'tasks'] });
        queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
      }
      showToast('Task updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update task', 'error');
    }
  });
};

export const useDeleteTurnTask = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.maintenance.turns.tasks.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'turns'] });
      showToast('Task deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete task', 'error');
    }
  });
};

// INSPECTION PHOTOS
export const useInspectionPhotos = (inspectionId: string | undefined) => {
  return useQuery({
    queryKey: ['maintenance', 'inspections', inspectionId, 'photos'],
    queryFn: () => api.maintenance.inspections.photos.list(inspectionId!),
    enabled: !!inspectionId,
  });
};

export const useAddInspectionPhoto = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ inspectionId, file, description }: { inspectionId: string; file: File; description?: string }) =>
      api.maintenance.inspections.photos.add(inspectionId, file, description),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections', data.entityId, 'photos'] });
      showToast('Photo added successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to add photo', 'error');
    }
  });
};

export const useDeleteInspectionPhoto = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (fileId: string) => api.maintenance.inspections.photos.delete(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections'] });
      showToast('Photo deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete photo', 'error');
    }
  });
};

// INSPECTION SCHEDULES
export const useInspectionSchedules = (filters?: { propertyId?: string; isActive?: boolean }) => {
  return useQuery({
    queryKey: ['maintenance', 'inspections', 'schedules', filters],
    queryFn: () => api.maintenance.inspections.schedules.list(filters),
  });
};

export const useCreateInspectionSchedule = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (schedule: Omit<InspectionSchedule, 'id' | 'createdAt' | 'updatedAt' | 'lastRunAt'>) =>
      api.maintenance.inspections.schedules.create(schedule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections', 'schedules'] });
      showToast('Inspection schedule created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create inspection schedule', 'error');
    }
  });
};

export const useUpdateInspectionSchedule = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<InspectionSchedule> }) =>
      api.maintenance.inspections.schedules.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections', 'schedules'] });
      showToast('Inspection schedule updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update inspection schedule', 'error');
    }
  });
};

export const useDeleteInspectionSchedule = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.maintenance.inspections.schedules.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance', 'inspections', 'schedules'] });
      showToast('Inspection schedule deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete inspection schedule', 'error');
    }
  });
};


